import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { StickyBanner } from "@/components/StickyBanner";
import { SEO } from "@/components/SEO";
import { SpanishHero } from "@/components/SpanishHero";
import { SpanishWhyUs } from "@/components/SpanishWhyUs";
import { SpanishServices } from "@/components/SpanishServices";
import { SpanishTestimonials } from "@/components/SpanishTestimonials";
import { SpanishFAQ } from "@/components/SpanishFAQ";
import { SpanishFinalCTA } from "@/components/SpanishFinalCTA";
import { LocationContext } from "@/lib/locations";

import { Guarantee } from "@/components/Guarantee";
import { PricingCalculator } from "@/components/PricingCalculator";
import { FrequencyDiscounts } from "@/components/FrequencyDiscounts";
import { InternalLinks } from "@/components/InternalLinks";
import { MapPin, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { cn } from "@/lib/utils";

const BOOKING_URL = "/book";

const NearbyAreas = () => {
  const { ref, isVisible } = useScrollAnimation();
  const areas = [
    { zone: "Berkeley Central", cities: "Downtown, Telegraph Ave, Shattuck Ave, UC Berkeley, Southside" },
    { zone: "North Berkeley", cities: "Gourmet Ghetto, Thousand Oaks, Solano Ave, Northbrae" },
    { zone: "South Berkeley", cities: "Elmwood, Claremont, Rockridge, Lorin District" },
    { zone: "West Berkeley", cities: "Marina, Aquatic Park, 4th Street, Oceanview" },
  ];
  return (
    <section className="py-16 bg-muted/30">
      <div className="container">
        <div ref={ref} className={cn("text-center mb-10 anim-fade-up", isVisible && "is-visible")}>
          <h2 className="text-2xl md:text-3xl font-bold mb-3">Vecindarios que Servimos en Berkeley</h2>
          <p className="text-muted-foreground">Limpieza profesional a domicilio en todos los vecindarios de Berkeley y alrededores.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {areas.map((area, i) => (
            <div
              key={i}
              className={cn("bg-background rounded-2xl p-6 border shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 text-center anim-fade-up", isVisible && "is-visible")}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <MapPin className="h-6 w-6 text-primary mx-auto mb-3" />
              <h3 className="font-bold text-primary mb-2">{area.zone}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{area.cities}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center flex flex-col sm:flex-row justify-center gap-4">
          <Button asChild size="lg" className="btn-pulse bg-primary text-primary-foreground hover:bg-primary-hover font-bold px-10 py-6 h-auto rounded-2xl shadow-lg">
            <a href={BOOKING_URL}>Reservar Ahora en Berkeley</a>
          </Button>
          <Button asChild size="lg" variant="outline" className="border-2 border-primary text-primary hover:bg-primary/5 hover:text-primary font-bold px-10 py-6 h-auto rounded-2xl">
            <a href={BOOKING_URL}>Ver Mi Precio al Instante</a>
          </Button>
        </div>
      </div>
    </section>
  );
};

const BerkeleySpanishPage = () => {
  return (
    <LocationContext.Provider value={{ slug: "berkeley", name: "Berkeley", type: "city", regionSlug: "alameda" }}>
      <SEO
        title="Limpieza del Hogar en Berkeley, CA — Uplevel Cleaning"
        description="Servicio profesional de limpieza del hogar en Berkeley, CA. Limpiamos apartamentos, casas y condominios cerca de UC Berkeley. Precios planos, reserva en 60 segundos, garantía del 200%."
        keywords="limpieza del hogar berkeley, servicio de limpieza berkeley ca, empleada doméstica berkeley, maid service berkeley español, limpieza de casas berkeley ca, limpieza near uc berkeley, limpieza apartamentos berkeley"
        url="https://uplevelcleaning.com/es/berkeley"
        locationName="Berkeley"
        regionSlug="alameda"
        lang="es"
        includeLocalBusiness={true}
        includeFAQ={true}
        breadcrumbs={[
          { name: "Inicio", url: "/" },
          { name: "En Español", url: "/es/sacramento" },
          { name: "Condado de Alameda", url: "/es/alameda" },
          { name: "Berkeley", url: "/es/berkeley" }
        ]}
        reviews={[
          { author: "Isabel C.", rating: 5, reviewBody: "Vivo cerca de UC Berkeley y buscaba un servicio de limpieza confiable. Uplevel Cleaning superó mis expectativas. Mi apartamento quedó perfecto.", datePublished: "2026-03-20" },
          { author: "Andrés M.", rating: 5, reviewBody: "Contraté la limpieza profunda para mi casa en el Elmwood District. El equipo fue muy profesional y detallista. ¡Totalmente recomendado!", datePublished: "2026-02-15" },
          { author: "Sofía L.", rating: 5, reviewBody: "Llevo 4 meses con el servicio quincenal en North Berkeley. Siempre puntuales y minuciosos. El mejor servicio de limpieza que he encontrado.", datePublished: "2026-01-28" }
        ]}
      />
      <div className="flex min-h-screen flex-col" lang="es">
        <StickyBanner />
        <Navbar />
        <main className="flex-1">
          <SpanishHero cityName="Berkeley" regionSlug="alameda" />

          <div className="bg-primary/5 border-b border-primary/10 py-4">
            <div className="container flex flex-wrap justify-center gap-6 text-sm font-medium text-foreground">
              {[
                "Berkeley · Oakland · Emeryville",
                "Rockridge · Elmwood · Northbrae",
                "Garantía del 200%",
                "Precios Transparentes",
                "Reserva en 60 Segundos",
              ].map((item, i) => (
                <span key={i} className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" /> {item}
                </span>
              ))}
            </div>
          </div>

          <SpanishWhyUs cityName="Berkeley" />
          <NearbyAreas />
          <SpanishServices />

          <FrequencyDiscounts />
          <PricingCalculator />
          <SpanishTestimonials region="oakland" />
          <Guarantee />
          <SpanishFAQ cityName="Berkeley" />
          <InternalLinks />
          <SpanishFinalCTA cityName="Berkeley" />
        </main>
        <Footer />
      </div>
    </LocationContext.Provider>
  );
};

export default BerkeleySpanishPage;
